import React, { useState, useEffect } from 'react';
import theme from './BuyerOptionPresetCard.module.scss';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import type { OptionPresetDto, OptionPresetImageUpload } from '@/services/option-preset';
import { useOptionPresetMutations } from '@/services/option-preset';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { useMergeState, useAsyncState } from '@/utils/hooks';
import { ValidationForm, ValidatingInput, ValidatingSelect } from '@/components/forms/validation';
import { getPricingFields } from '@/services/projects';
import { useSelector } from 'react-redux';
import { IState } from '@/state/IState';
import { toNullableNumber } from '@/utils/html';
import { Button, ButtonBar, SlideToggle } from '@ygtechnologies/rubus-ui';
import { MarkdownEditor } from '@ygtechnologies/bygr-frontend-component-library';
import { SortableImageUpload } from '@/components/SortableImageUpload';
import { getOptionPresetImageUrl, getOptionPresetImageThumbnailUrl } from '@/services/option-preset/option-preset-image';
import { getProjectFeeSets } from '@/services/fees';

export interface BuyerOptionPresetCardEditProps {
    preset?: OptionPresetDto;
    presetGroupId?: number;
    onEditSave(): void;
    onEditCancel(): void;
}

interface EditState {
    name: string;
    description: string;
    images: OptionPresetImageUpload[];
    hasDirectCost: boolean;
    price: number | null;
    pricingFieldId: number | null;
    projectFeeSetId: number | null;
    feeSetId: number | null;
}

function getEditStateFromPreset(preset?: OptionPresetDto): EditState {
    if (!preset) {
        return {
            name: '',
            description: '',
            images: [],
            hasDirectCost: false,
            price: null,
            pricingFieldId: null,
            projectFeeSetId: null,
            feeSetId: null,
        };
    }

    return {
        name: preset.name,
        description: preset.description,
        images: preset.images,
        hasDirectCost: preset.hasDirectCost,
        price: preset.price,
        pricingFieldId: preset.pricingFieldId,
        projectFeeSetId: preset.projectFeeSetId,
        feeSetId: preset.feeSetId,
    };
}

export const BuyerOptionPresetCardEdit: React.FC<BuyerOptionPresetCardEditProps> = ({
    preset,
    presetGroupId,
    onEditSave,
    onEditCancel,
}) => {
    const projectId = useSelector((state: IState) => state.global.activeProject!.id);
    const [saving, setSaving] = useState(false);
    const [editState, mergeEditState, setEditState] = useMergeState<EditState>(getEditStateFromPreset(preset));
    const [pricingFields] = useAsyncState(() => getPricingFields(projectId), [projectId], null);
    const [projectFeeSets] = useAsyncState(() => getProjectFeeSets(projectId), [projectId], null);

    const presetMutations = useOptionPresetMutations();

    useEffect(() => {
        setEditState(getEditStateFromPreset(preset));
    }, [preset, setEditState]);

    useEffect(() => {
        if (projectFeeSets && !editState.projectFeeSetId) {
            mergeEditState({ projectFeeSetId: projectFeeSets.find((x) => x.isDefault)?.id });
        }
    }, [projectFeeSets, editState.projectFeeSetId, mergeEditState]);

    const handleCancelClick = () => {
        triggerHandler(onEditCancel);
    };
    const handleSaveClick = async () => {
        if (!preset && !presetGroupId) {
            throw Error('Must provide either preset or presetGroupId');
        }

        setSaving(true);

        try {
            if (preset) {
                await presetMutations.update.mutateAsync({
                    ...editState,
                    id: preset.id,
                });
            } else if (presetGroupId) {
                await presetMutations.create.mutateAsync({ ...editState, optionPresetGroupId: presetGroupId });
            }
        } finally {
            setSaving(false);
        }
        triggerHandler(onEditSave);
    };

    // const selectedProjectFeeSet = projectFeeSets?.find((x) => x.id === editState.projectFeeSetId);

    return (
        <ValidationForm onSubmit={handleSaveClick} className={theme.editForm}>
            <dl className="dl-vertical">
                <dt>
                    <Trans>Name</Trans>
                </dt>
                <dd>
                    <ValidatingInput
                        required
                        placeholder={t`Name`}
                        type="text"
                        className="form-control"
                        value={editState.name}
                        onChange={(e) => mergeEditState({ name: e.target.value })}
                    />
                </dd>

                <dt>
                    <Trans>Description</Trans>
                </dt>
                <dd>
                    <MarkdownEditor
                        className="form-control"
                        markdown={editState.description}
                        onChange={(value) => mergeEditState({ description: value })}
                        minHeight="200px"
                    />
                </dd>

                <hr />

                <dt>
                    <Trans>Pricing</Trans>
                </dt>
                <dd>
                    <SlideToggle
                        checked={editState.hasDirectCost}
                        onChange={(e) => mergeEditState({ hasDirectCost: e.target.checked })}>
                        <Trans>Has direct cost</Trans>
                    </SlideToggle>
                    {editState.hasDirectCost && (
                        <div className={theme.pricingEditor}>
                            <div className="form columns-1 labels-20">
                                <label>
                                    <Trans>Price</Trans>
                                    <ValidatingInput
                                        className="form-control"
                                        value={editState.price ?? ''}
                                        onChange={(e) => mergeEditState({ price: toNullableNumber(e.target.value) })}
                                        required
                                    />
                                </label>
                                {/* Todo: */}
                                <i>Not included in price right now</i>
                                <label>
                                    <Trans>Fees</Trans>
                                    {projectFeeSets && (
                                        <ValidatingSelect
                                            className="form-control"
                                            value={editState.projectFeeSetId || ''}
                                            onChange={(e) =>
                                                mergeEditState({ projectFeeSetId: toNullableNumber(e.target.value) })
                                            }>
                                            {projectFeeSets.map((x) => (
                                                <option key={x.id} value={x.id}>
                                                    {x.name}
                                                </option>
                                            ))}
                                        </ValidatingSelect>
                                    )}
                                </label>

                                <label>
                                    <Trans>Pricing variable</Trans>
                                    {pricingFields && (
                                        <ValidatingSelect
                                            className="form-control"
                                            value={editState.pricingFieldId || ''}
                                            onChange={(e) =>
                                                mergeEditState({ pricingFieldId: toNullableNumber(e.target.value) })
                                            }>
                                            <option>{t`None`}</option>
                                            {pricingFields.map((x) => (
                                                <option key={x.id} value={x.id}>
                                                    {x.name}
                                                </option>
                                            ))}
                                        </ValidatingSelect>
                                    )}
                                </label>
                            </div>

                            {/* <div>
                                {selectedProjectFeeSet && (
                                    <FeePreview
                                        basePrice={editState.price ?? 100}
                                        configuration={selectedProjectFeeSet.fees}
                                        quantity={1}
                                    />
                                )}
                            </div> */}
                        </div>
                    )}
                </dd>

                <hr />

                <dt>
                    <Trans>Images</Trans>
                </dt>
                <dd>
                    <SortableImageUpload
                        onImagesChange={(images) => mergeEditState({ images: images })}
                        files={editState.images}
                        getThumbnailUrl={getOptionPresetImageThumbnailUrl}
                        getUrl={getOptionPresetImageUrl}
                    />
                </dd>
            </dl>

            <ButtonBar>
                <ButtonBar.Right>
                    <Button onClick={handleCancelClick} variant="tertiary" disabled={saving}>
                        <Trans>Cancel</Trans>
                    </Button>
                    <Button type="submit" variant="primary" loading={saving}>
                        <Trans>Save</Trans>
                    </Button>
                </ButtonBar.Right>
            </ButtonBar>
        </ValidationForm>
    );
};
